"use client";

// Holds the resolved locale AND its strings for client components. Read via
// useAppLocale() / useAppStrings() — no useState/useEffect → no flash of
// English content on /app/* or /login. Both values are set server-side: the
// layout reads the `a2g_locale` cookie and calls getAppStrings once.
//
// The strings arrive as DATA, not as code. Client components used to call
// getAppStrings(locale) themselves, which meant importing the dictionary —
// and since it is keyed by a runtime value, the bundler shipped all eleven
// languages to every visitor. Passing the one resolved locale's strings
// through here keeps the other ten out of the browser entirely.

import { createContext, useContext, type ReactNode } from "react";
import type { AppLocale, AppStrings } from "@/lib/i18n/app-strings";

type LocaleValue = { locale: AppLocale; strings: AppStrings };

const AppLocaleContext = createContext<LocaleValue | null>(null);

export function AppLocaleProvider({
  initialLocale,
  strings,
  children,
}: {
  initialLocale: AppLocale;
  strings: AppStrings;
  children: ReactNode;
}) {
  // The value is derived entirely from props the server rendered, so it is
  // referentially stable for the life of the layout; no memo needed.
  return (
    <AppLocaleContext.Provider value={{ locale: initialLocale, strings }}>
      {children}
    </AppLocaleContext.Provider>
  );
}

function useLocaleValue(): LocaleValue {
  const value = useContext(AppLocaleContext);
  if (!value) {
    throw new Error("useAppLocale/useAppStrings must be rendered inside AppLocaleProvider");
  }
  return value;
}

/** The locale the server resolved for this request. */
export function useAppLocale(): AppLocale {
  return useLocaleValue().locale;
}

/** The strings for that locale — the only locale sent to this browser. */
export function useAppStrings(): AppStrings {
  return useLocaleValue().strings;
}
